import { Checkbox, CloseButton, Menu, Table } from '@mantine/core';
import {
	IconBarcode,
	IconChevronDown,
	IconChevronUp,
	IconInfoCircle,
	IconShoppingBag,
	IconTrash,
} from '@tabler/icons-react';
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DeleteModal } from '../../../components/DeleteModal';
import { KeyedRow } from '../../../components/KeyedRow';
import { TableRowCollapsible } from '../../../components/TableRowCollapsible';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { useModal } from '../../../hooks/useModal';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import {
	formatToCurrency,
	formatToCurrencyOrDefault,
} from '../../../util/formatting';
import { undefinedCheck } from '../../../util/undefinedCheck';
import type { ProductDto, UpsertProductDto } from '../types/ProductDto';
import { BarcodesModal } from './BarcodesModal';
import { EditProductModal } from './EditProductModal';

interface ProductRowProps {
	originalProduct: ProductDto;
	triggerListUpdate: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
	isSelected: boolean;
	onToggle: () => void;
}

export function ProductRow({
	originalProduct,
	triggerListUpdate,
	setIsLoading,
	isSelected,
	onToggle,
}: ProductRowProps) {
	const { organisation } = useParams();
	const navigate = useNavigate();

	const [productOpen, setProductOpen] = useState(false);
	const [product, setProduct] = useState(originalProduct);

	const editModal = useModal();
	const barcodesModal = useModal();
	const deleteModal = useModal();

	const deleteProductMutation = mutate<ProductDto>(
		`${organisation}/product/delete/${product.skuid}`,
	);

	useEffect(() => {
		setIsLoading(deleteProductMutation.isLoading);
	}, [deleteProductMutation.isLoading]);

	const encodedUri = encodeURIComponent(product.skuid);

	return (
		<>
			<BarcodesModal
				isOpened={barcodesModal.isOpen}
				close={barcodesModal.close}
				skuid={product.skuid}
			/>
			<EditProductModal
				isOpened={editModal.isOpen}
				close={editModal.close}
				product={convertProduct(product)}
				updateProductRecord={(p) => {
					setProduct(p);
				}}
			/>
			<DeleteModal
				isOpened={deleteModal.isOpen}
				close={deleteModal.close}
				titleType={'Product'}
				itemDescription={product.skuid}
				extraInfo={'All branch stocks & barcodes will also be removed.'}
				mutation={deleteProductMutation}
				onComplete={() => triggerListUpdate()}
			/>
			<KeyedRow key={product.skuid}>
				<Table.Td>
					<Checkbox
						color={'medepos.1'}
						checked={isSelected}
						onChange={onToggle}
					/>
				</Table.Td>
				<Table.Td>{undefinedCheck(product.skuid)}</Table.Td>
				<Table.Td>{undefinedCheck(product.description)}</Table.Td>
				<Table.Td>{formatToCurrencyOrDefault(product.incVatPrice)}</Table.Td>
				<Table.Td>{undefinedCheck(product.group)}</Table.Td>
				<Table.Td>{undefinedCheck(product.department)}</Table.Td>
				<Table.Td>{undefinedCheck(product.supplier)}</Table.Td>
				<Table.Td>{undefinedCheck(product.vatRate)}</Table.Td>
				<Table.Td>
					<CloseButton
						icon={
							!productOpen ? (
								<IconChevronDown {...defaultIconStyle} />
							) : (
								<IconChevronUp {...defaultIconStyle} />
							)
						}
						onClick={() => setProductOpen(!productOpen)}
					/>
				</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Menu.Item
							leftSection={<IconShoppingBag {...defaultIconStyle} />}
							onClick={() => navigate(encodedUri)}
						>
							View Product
						</Menu.Item>
						<Menu.Item
							leftSection={<IconInfoCircle {...defaultIconStyle} />}
							onClick={() => editModal.open()}
						>
							Edit Product
						</Menu.Item>
						<Menu.Item
							leftSection={<IconBarcode {...defaultIconStyle} />}
							onClick={() => barcodesModal.open()}
						>
							Barcodes
						</Menu.Item>
						<Menu.Item
							leftSection={<IconTrash {...defaultIconStyle} />}
							c={'red'}
							onClick={() => deleteModal.open()}
						>
							Delete
						</Menu.Item>
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
			<TableRowCollapsible isOpen={productOpen} columnRows={12}>
				<Table>
					<Table.Thead>
						<KeyedRow>
							<Table.Th>Case Cost</Table.Th>
							<Table.Th>Av. Case Cost</Table.Th>
							<Table.Th>Single Cost</Table.Th>
							<Table.Th>Av. Single Cost</Table.Th>
							<Table.Th>Case Quantity</Table.Th>
							<Table.Th>Item Size</Table.Th>
						</KeyedRow>
					</Table.Thead>
					<Table.Tbody>
						<KeyedRow>
							<Table.Td>{formatToCurrency(originalProduct.caseCost)}</Table.Td>
							<Table.Td>
								{formatToCurrency(originalProduct.averageCaseCost)}
							</Table.Td>
							<Table.Td>
								{formatToCurrency(originalProduct.singleCost)}
							</Table.Td>
							<Table.Td>
								{formatToCurrency(originalProduct.averageSingleCost)}
							</Table.Td>
							<Table.Td>{originalProduct.caseQuantity}</Table.Td>
							<Table.Td>{undefinedCheck(originalProduct.itemSize)}</Table.Td>
						</KeyedRow>
					</Table.Tbody>
				</Table>
			</TableRowCollapsible>
		</>
	);
}

export const convertProduct = (product: ProductDto): UpsertProductDto => {
	return {
		description: product.description,
		skuid: product.skuid,
		brand: product.brand,
		model: product.model,
		groupId: product.groupId,
		departmentId: product.departmentId,
		supplierId: product.supplierId,
		vatRateId: product.vatRateId,
		caseCost: product.caseCost, // Optional fields are retained as-is
		caseQuantity: product.caseQuantity,
		stockQuantity: product.stockQuantity,
		orderQuantity: product.orderQuantity,
		singleCost: product.singleCost,
		barcode: product.barcode,
	};
};
